import React, { useState, useEffect, useRef } from 'react';
import axios, { decryptData } from '../Axiosinstance';
import { useParams } from 'react-router-dom';
import {
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Modal,
    Typography,
    Button,
    InputAdornment,
    CircularProgress,
    useMediaQuery,
    useTheme, Chip, Stack,
} from '@mui/material';
import {
    Search, Visibility, CalendarToday, Star,
    Person, Close, RateReview
} from '@mui/icons-material';

const Feedback = () => {
    const { intakeId } = useParams();
    const intakeId1 = decryptData(decodeURIComponent(intakeId))
    const [feedbackData, setFeedbackData] = useState({ questions: {}, responses: [] });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [horizontalScrollVisible, setHorizontalScrollVisible] = useState(false);
    const scrollContainerRef = useRef(null);

    const handleHorizontalScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setHorizontalScrollVisible(scrollLeft > 0 || scrollWidth > clientWidth);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            handleHorizontalScroll();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check scroll on content change
    useEffect(() => {
        handleHorizontalScroll();
    }, [selectedFeedback]);

    useEffect(() => {
        const fetchFeedbackData = async () => {
            try {
                const response = await axios.get(`/api/feedback/intake/${intakeId1}`);
                setFeedbackData(response.data);

                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchFeedbackData();
    }, [intakeId1]);



    const handleOpenModal = (feedback) => {
        setSelectedFeedback(feedback);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const filteredResponses = feedbackData.responses.filter((item) =>
        item.studentName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }



    return (
        <Box sx={{ p: isMobile ? 1 : 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                <Typography variant="h5" sx={{ color: '#111827' }}>
                    Student Feedback
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search students..."
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: '#9ca3af' }} />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '12px',
                            backgroundColor: 'white',
                            width: isMobile ? '100%' : 200
                        }
                    }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table striped bordered hover responsive style={{ width: '100%', tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow >
                            <TableCell sx={{ fontWeight: 'bold' }}>S.No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Submit At</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredResponses.length > 0 ? (
                            filteredResponses.map((item, index) => (
                                <TableRow key={item.studentFeedbackId} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{item.studentName}</TableCell>
                                    <TableCell>  <Chip
                                        icon={<CalendarToday sx={{ fontSize: '16px' }} />}
                                        label={new Date(item.submittedAt).toLocaleDateString()}
                                        size="small"
                                        sx={{ borderRadius: '8px' }}
                                    /></TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Visibility />}
                                            onClick={() => handleOpenModal(item)}
                                            sx={{
                                                borderRadius: '8px',
                                                textTransform: 'none'
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Star sx={{ fontSize: 60, color: '#a5e7eb', mb: 2 }} />
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        No feedback found
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#6b7280', mb: 2 }}>
                                        {searchTerm ? 'Try a different search term' : 'No submissions yet'}
                                    </Typography>
                                    {searchTerm && (
                                        <Button
                                            variant="outlined" 
                                            color="primary"    
                                            onClick={() => setSearchTerm('')}
                                            startIcon={<Close />}
                                        >
                                            Clear search
                                        </Button>


                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                </Table>
            </TableContainer>



            <Modal
                open={openModal}
                onClose={handleCloseModal}
                sx={{
                    // This helps with scroll detection
                    overflow: 'hidden'
                }}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isMobile ? '95%' : '60%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    borderRadius: '12px',
                    boxShadow: 24,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    outline: 'none',
                    overflow: 'hidden',
                }}>
                    {/* Horizontal scroll indicator (top gradient) */}
                    <Box sx={{
                        height: 4,
                        background: 'linear-gradient(to right, #FFD700, transparent)',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        opacity: horizontalScrollVisible ? 0 : 0,
                        transition: 'opacity 0.3s ease',
                        zIndex: 10,
                        pointerEvents: 'none'
                    }} />

                    {/* Scrollable content area */}
                    <Box
                        ref={scrollContainerRef}
                        onScroll={handleHorizontalScroll}
                        sx={{
                            overflow: 'auto',
                            scrollbarWidth: 'none',  // Firefox
                            '&::-webkit-scrollbar': {
                                display: 'none'  // Chrome, Safari
                            },
                            flex: 1,
                        }}
                    >
                        {selectedFeedback && (
                            <>
                                <Box sx={{
                                    p: 3,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderColor: 'divider',
                                    backgroundColor: 'primary.light',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: 'primary.contrastText',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2
                                }}>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            Feedback Details
                                        </Typography>
                                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                            <Chip
                                                icon={<Person />}
                                                label={selectedFeedback.studentName}
                                                size="small"
                                            />
                                            <Chip
                                                icon={<CalendarToday />}
                                                label={new Date(selectedFeedback.submittedAt).toLocaleDateString()}
                                                size="small"
                                            />
                                        </Stack>
                                    </Box>
                                    <IconButton onClick={handleCloseModal}>
                                        <Close />
                                    </IconButton>
                                </Box>

                                <Box sx={{
                                    p: 3,
                                    // Ensure content can cause horizontal overflow if needed
                                    minWidth: 'min-content'
                                }}>
                                    <Typography variant="h6" sx={{ mb: 3 }}>
                                        Questions & Answers
                                    </Typography>

                                    {selectedFeedback.answers && Object.keys(selectedFeedback.answers).length > 0 ? (
                                        <Stack spacing={3}>
                                            {Object.entries(selectedFeedback.answers).map(([questionId, answerText], index) => (
                                                <Box key={questionId}>
                                                    <Typography variant="subtitle2" sx={{
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        mb: 1,
                                                        // Prevent text wrapping to test horizontal scroll
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {index + 1}. {feedbackData.questions[questionId]}
                                                    </Typography>
                                                    <Box sx={{
                                                        p: 2,
                                                        borderRadius: '8px',
                                                        backgroundColor: '#f9fafb',
                                                        borderLeft: '3px solid #6366f1',
                                                        // Long content that might cause horizontal scroll
                                                        overflowX: 'auto',
                                                        scrollbarWidth: 'none',
                                                        '&::-webkit-scrollbar': { display: 'none' }
                                                    }}>
                                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                                            {answerText || 'No response provided'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            py: 4,
                                            gap: 1
                                        }}>
                                            <RateReview sx={{ fontSize: 60, color: '#e5e7eb' }} />
                                            <Typography variant="body1" sx={{ color: '#6b7280' }}>
                                                No answers available for this feedback
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.paper',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            variant="contained"
                            onClick={handleCloseModal}
                            startIcon={<Close />}
                            sx={{ borderRadius: '8px' }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default Feedback;